import { Component, Element, h, Host, Listen, Method, Prop, readTask, State, Watch, writeTask } from '@stencil/core';

/**
 * Animate a number from 0 to a given value by sliding the numbers up or down.
 *
 * <lg-num-slide-example></lg-num-slide-example>
 */
@Component({
  tag: 'lg-num-slide',
  styleUrl: 'lg-num-slide.scss',
})
export class LgNumSlide {
  @Element() el: HTMLElement;

  io?: IntersectionObserver;
  delayedStartTimeout?: number | NodeJS.Timeout;

  /**
   * The number to count up to.
   * Can also include non-numeric characters.
   */
  @Prop() value: number | string;

  /**
   * Don't start automatically when in the viewport.
   */
  @Prop() noAutoplay = false;

  /**
   * Restart the animation when the component is in the viewport again.
   */
  @Prop() restart = false;

  @Watch('restart')
  restartChanged() {
    if (this.restart) {
      this.waitUntilVisible();
      return;
    }

    if (this.showFinalNumber) {
      // only stop if we're already showing the final number
      this.stopWaitUntilVisible();
    }
  }

  /**
   * Don't restart the animation on click.
   */
  @Prop() noClick = false;

  /**
   * Animate the numbers down instead of up.
   */
  @Prop() reversed = false;

  /**
   * The delay (in ms) until the animation starts after the component is fully in the viewport.
   */
  @Prop() delay = 0;

  /**
   * Whether the final number should be shown.
   * Changing this to `true` will start the animation.
   */
  @State() showFinalNumber = false;

  @Listen('click')
  onClick() {
    this.start();
  }

  /**
   * Manually start the animation.
   */
  @Method()
  async start(): Promise<void> {
    if (!this.showFinalNumber) {
      this.showFinalNumber = true;
      return;
    }

    // animation already started
    if (this.noClick) {
      return;
    }

    // reset number
    this.showFinalNumber = false;

    // wait until rerender
    writeTask(() => {
      readTask(() => {
        // animate number again
        this.start();
      });
    });
    return;
  }

  /**
   * Reset the number.
   */
  @Method()
  async reset(): Promise<void> {
    this.showFinalNumber = false;
  }

  connectedCallback() {
    if (undefined === this.value) {
      throw new Error('The value attribute is required.');
    }

    if (this.noAutoplay) {
      return;
    }

    this.waitUntilVisible();
  }

  disconnectedCallback() {
    this.stopWaitUntilVisible();
  }

  waitUntilVisible() {
    if (this.io) {
      return;
    }

    this.io = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.intersectionRatio === 1)) {
          // component is fully in viewport
          if (!this.showFinalNumber) {
            // don't start again if we're already showing the final number
            if (this.delay > 0) {
              clearTimeout(this.delayedStartTimeout);
              this.delayedStartTimeout = setTimeout(() => {
                this.start();
              }, this.delay);
            } else {
              this.start();
            }
          }

          if (!this.restart) {
            this.stopWaitUntilVisible();
          }
        } else if (entries.some(entry => entry.intersectionRatio === 0)) {
          // component is fully out of viewport
          if (this.showFinalNumber && !this.restart) {
            // we're done and don't want to restart
            this.stopWaitUntilVisible();
            return;
          }

          // reset number to replay again later
          this.showFinalNumber = false;
          // prevent delayed start from overwriting
          clearTimeout(this.delayedStartTimeout);
        }
      },
      {
        threshold: this.restart ? [0, 1] : 1,
        // rootMargin: '-100px',
      },
    );

    this.io.observe(this.el);
  }

  stopWaitUntilVisible() {
    if (!this.io) {
      return;
    }

    this.io.disconnect();
    this.io = undefined;
  }

  generateRow(selectedNumber?: string) {
    const numbers = new Array(10).fill(0).map((_, i) => (this.reversed ? 9 - i : i));

    return (
      <span
        class={{ column: true, animated: undefined !== selectedNumber }}
        style={{
          '--selected': selectedNumber || '0',
        }}
      >
        <span class="column-inner">
          {numbers.map(num => (
            <span>{num}</span>
          ))}
        </span>
      </span>
    );
  }

  render() {
    return (
      <Host class={{ reversed: this.reversed }}>
        {
          this.value
            .toString()
            .split('')
            .map(num => (isNumeric(num) ? this.generateRow(this.showFinalNumber ? num : undefined) : num))
        }
      </Host>
    );
  }
}

function isNumeric(num: string): boolean {
  return num.match(/^[0-9]$/) !== null;
}
